import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
// Material UI imports
import Grid from '@mui/material/Grid';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
// Project components
import DetailModal from 'components/Common/DetailModal';
import { HButton } from 'BaseComponents';
// Constants and utils
import {
  CLOUD_TO_REGION_TO_SUBREGION_MAP,
  REGION_LIST,
  SUB_REGION_LIST,
} from 'components/CleanRooms/constants';


const findConfigValue = (config = [], paramName) => {
  const param = config.find(item => item && item.name === paramName);
  return param ? param.value : '';
};

const CONFIG_NAMES = {
  CLOUD: 'CLOUD',
  REGION: 'REGION',
  SUB_REGION: 'SUB_REGION',
};

// Types of operations for the modal
const OPERATION_TYPES = {
  ADD: 'Add',
  UPDATE: 'Edit',
};

/**
 * EditCloudParams Component for editing cloud, region and subregion parameters
 */
const EditCloudParams = ({
  modalOperation, open, onClose, addnewconf, configList, onSaveParameters,
}) => {
  const [selectedCloud, setSelectedCloud] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedSubRegion, setSelectedSubRegion] = useState('');
  const availableRegions = useMemo(() => {
    if (!selectedCloud || !CLOUD_TO_REGION_TO_SUBREGION_MAP[selectedCloud]) {
      return [];
    }
    return Object.keys(CLOUD_TO_REGION_TO_SUBREGION_MAP[selectedCloud]);
  }, [selectedCloud]);
  const availableSubRegions = useMemo(() => {
    if (!selectedCloud
      || !selectedRegion
      || !CLOUD_TO_REGION_TO_SUBREGION_MAP[selectedCloud]
      || !CLOUD_TO_REGION_TO_SUBREGION_MAP[selectedCloud][selectedRegion]) {
      return [];
    }
    return CLOUD_TO_REGION_TO_SUBREGION_MAP[selectedCloud][selectedRegion];
  }, [selectedCloud, selectedRegion]);
  // Initialize form when modal opens or configList/addnewconf changes
  useEffect(() => {
    const cloud = findConfigValue(configList, CONFIG_NAMES.CLOUD);
    const region = findConfigValue(configList, CONFIG_NAMES.REGION);
    const subRegion = findConfigValue(configList, CONFIG_NAMES.SUB_REGION);
    if (cloud) {
      setSelectedCloud(cloud);
    }
    else if (modalOperation === OPERATION_TYPES.ADD
      && addnewconf
      && addnewconf.name === CONFIG_NAMES.CLOUD) {
      setSelectedCloud(addnewconf.value || '');
    }
    setTimeout(() => {
      if (region) {
        setSelectedRegion(region);
      }
      else if (modalOperation === OPERATION_TYPES.ADD
        && addnewconf
        && addnewconf.name === CONFIG_NAMES.REGION) {
        setSelectedRegion(addnewconf.value || '');
      }
      setTimeout(() => {
        if (subRegion) {
          setSelectedSubRegion(subRegion);
        }
        else if (modalOperation === OPERATION_TYPES.ADD
          && addnewconf
          && addnewconf.name === CONFIG_NAMES.SUB_REGION) {
          setSelectedSubRegion(addnewconf.value || '');
        }
      }, 0);
    }, 0);
  }, [configList, modalOperation, addnewconf]);

  useEffect(() => {
    if (availableSubRegions.length === 0 && selectedSubRegion) {
      setSelectedSubRegion('');
    }
  }, [availableSubRegions, selectedSubRegion]);

  const handleCloudChange = (event) => {
    setSelectedCloud(event.target.value);
  };

  const handleRegionChange = (event) => {
    setSelectedRegion(event.target.value);
  };

  const handleSubRegionChange = (event) => {
    setSelectedSubRegion(event.target.value);
  };

  const onEdit = () => {
    const parameters = [
      {
        name: CONFIG_NAMES.CLOUD,
        value: selectedCloud,
      },
      {
        name: CONFIG_NAMES.REGION,
        value: selectedRegion,
      },
      {
        name: CONFIG_NAMES.SUB_REGION,
        value: selectedSubRegion,
      },
    ];
    if (typeof onSaveParameters === 'function') {
      onSaveParameters(parameters);
    }
    onClose();
  };

  return (
    <DetailModal
      modalHeading={`${modalOperation} Cloud Parameters`}
      onOpen={open}
      onClose={onClose}
    >
      <Box p={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl
              fullWidth
            >
              <InputLabel id='cloud-select-label'>Cloud</InputLabel>
              <Select
                labelId='cloud-select-label'
                id='cloud-select'
                value={selectedCloud}
                label='Cloud'
                onChange={handleCloudChange}
              >
                {Object.keys(CLOUD_TO_REGION_TO_SUBREGION_MAP).map((cloud) => (
                  <MenuItem key={cloud} value={cloud}>
                    {cloud}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Select a cloud provider</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl
              fullWidth
              disabled={!selectedCloud}
            >
              <InputLabel id='region-select-label'>Region</InputLabel>
              <Select
                labelId='region-select-label'
                id='region-select'
                value={selectedRegion}
                label='Region'
                onChange={handleRegionChange}
              >
                {availableRegions.length > 0 ? (
                  availableRegions.map((region) => (
                    <MenuItem key={region} value={region}>
                      {REGION_LIST[region]?.key}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled value=''>
                    No regions available for this cloud
                  </MenuItem>
                )}
              </Select>
              <FormHelperText>Select a region</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl
              fullWidth
              disabled={!selectedRegion}
            >
              <InputLabel id='subregion-select-label'>Sub-Region</InputLabel>
              <Select
                labelId='subregion-select-label'
                id='subregion-select'
                value={selectedSubRegion}
                label='Sub-Region'
                onChange={handleSubRegionChange}
              >
                {availableSubRegions.length > 0 ? (
                  availableSubRegions.map((subRegion) => (
                    <MenuItem key={subRegion} value={subRegion}>
                      {SUB_REGION_LIST[subRegion]?.key}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled value=''>
                    No sub-regions available
                  </MenuItem>
                )}
              </Select>
              <FormHelperText>
                {availableSubRegions.length > 0
                  ? 'Select a sub-region'
                  : 'No sub-regions available for this region'}
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
      <Box
        display='flex'
        justifyContent='flex-end'
        gap={1}
        sx={{ mt: 2, p: 2, borderTop: '1px solid #eee' }}
      >
        <HButton
          onClick={onEdit}
          size='small'
          color='primary'
          variant='contained'
          disabled={!selectedCloud || !selectedRegion}
        >
          Save
        </HButton>
        <HButton
          onClick={onClose}
          size='small'
          variant='outlined'
        >
          Cancel
        </HButton>
      </Box>
    </DetailModal>
  );
};

export default EditCloudParams;
